import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import WorkCard from "@/components/feature/WorkCard";
import { workById, works } from "@/mocks/works";
import { categoryBySlug } from "@/mocks/categories";
import { siteInfo } from "@/mocks/site";
import NotFound from "@/pages/NotFound";

export default function WorkPage() {
  const { id = "" } = useParams();
  const work = workById(id);
  const [collected, setCollected] = useState(false);
  const [reading, setReading] = useState<string | null>(null);

  useSeo({
    title: work
      ? `${work.title}漫画在线看_${work.title}最新章节追更`
      : "作品未找到 - 汗汗漫画",
    description: work
      ? `${work.title}是汗汗漫画${categoryBySlug(work.category)?.name ?? ""}类人气漫画，${work.intro}在线看最新章节，跟汗汗漫画一起追更烧脑反转佳作。`
      : siteInfo.description,
    keywords: work
      ? `汗汗漫画,${work.title},${work.title}漫画,${work.title}漫画在线看`
      : undefined,
    jsonLd: work
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: work.title,
            author: work.author,
            url: `${siteInfo.url}/work/${work.id}`,
            genre: work.tags,
            inLanguage: "zh-CN",
            description: work.intro,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: work.rating,
              bestRating: 10,
              ratingCount: "9999",
            },
          },
        ]
      : undefined,
  });

  if (!work) {
    return <NotFound />;
  }

  const cat = categoryBySlug(work.category);
  const related = works.filter((w) => w.id !== work.id).slice(0, 4);
  const latestChapter = work.chapters[0];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
      {/* 面包屑 */}
      <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
        <Link to="/" className="cursor-pointer transition-colors hover:text-accent-300">首页</Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <Link to={`/category/${work.category}`} className="cursor-pointer transition-colors hover:text-accent-300">
          {cat?.name}漫画
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="font-medium text-accent-300">{work.title}</span>
      </nav>

      {/* 作品信息 */}
      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
        {/* 封面 */}
        <div className="mx-auto w-full max-w-[280px] lg:mx-0">
          <div className="relative overflow-hidden rounded-lg border border-background-200/60 bg-background-100">
            <img
              src={work.cover}
              alt={`${work.title}漫画封面`}
              title={`${work.title}漫画在线阅读`}
              className="aspect-[5/7] w-full object-cover object-top"
            />
            <span className="absolute left-3 top-3 rounded-full bg-background-950/80 px-3 py-1 text-xs font-medium text-accent-300 backdrop-blur-sm">
              {work.status}
            </span>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setCollected((v) => !v)}
              className={`inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-semibold transition-colors ${
                collected
                  ? "bg-secondary-500 text-background-50 hover:bg-secondary-600"
                  : "border border-background-300/60 text-foreground-100 hover:border-accent-300/60 hover:text-accent-200"
              }`}
            >
              <i
                className={collected ? "ri-bookmark-fill" : "ri-bookmark-line"}
                aria-hidden="true"
              ></i>
              {collected ? "已收藏 · 更新可追" : "收藏追更"}
            </button>
            <a
              href="#chapters"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line" aria-hidden="true"></i>
              开始阅读
            </a>
          </div>
        </div>

        {/* 详情 */}
        <div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <h1 className="font-heading text-3xl font-black text-foreground-50 md:text-4xl">
              {work.title}
            </h1>
            <div className="flex items-center gap-2 rounded-full border border-accent-300/40 bg-accent-500/10 px-4 py-1.5 text-sm font-semibold text-accent-200">
              <i className="ri-star-fill" aria-hidden="true"></i>
              {work.rating.toFixed(1)} 分
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span>作者：{work.author}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {work.views} 阅读
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-bookmark-3-line" aria-hidden="true"></i>
              {work.collected} 收藏
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {work.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-secondary-500/20 px-3 py-1 text-xs text-secondary-200"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="mt-6 rounded-lg border border-background-200/60 bg-background-100 p-5">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-200">
              <i className="ri-quill-pen-line text-primary-400" aria-hidden="true"></i>
              作品简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-300 md:text-base">
              {work.intro}
            </p>
            <ul className="mt-4 space-y-2 border-l-2 border-primary-500/50 pl-4">
              {work.introDetail.map((line) => (
                <li key={line} className="text-sm leading-relaxed text-foreground-500">
                  {line}
                </li>
              ))}
            </ul>
          </div>

          {/* 最新章节提示 */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-accent-300/30 bg-accent-500/5 p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-flashlight-fill" aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-100">
                  最新更新：第 {latestChapter.no} 话 {latestChapter.title}
                </p>
                <p className="text-xs text-foreground-500">
                  {latestChapter.update} · 点击下方章节立即阅读
                </p>
              </div>
            </div>
          </div>

          {/* 章节列表 */}
          <div id="chapters" className="mt-6">
            <h2 className="flex items-center justify-between gap-3 font-heading text-xl font-bold text-foreground-50">
              <span>章节列表</span>
              <span className="text-xs font-normal text-foreground-500">
                共 {work.chapters.length} 话
              </span>
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {work.chapters.map((ch) => (
                <button
                  key={ch.no}
                  type="button"
                  onClick={() => setReading(ch.title)}
                  className="group flex cursor-pointer items-center justify-between gap-3 rounded-md border border-background-200/60 bg-background-100 px-4 py-3 text-left transition-colors hover:border-accent-300/50 hover:bg-background-200"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary-500/15 font-heading text-sm font-bold text-primary-400">
                      {ch.no}
                    </span>
                    <span className="truncate text-sm text-foreground-200">
                      第 {ch.no} 话 {ch.title}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-foreground-500">
                    {ch.update}
                    <i className="ri-play-circle-line text-accent-300" aria-hidden="true"></i>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 阅读提示浮层 */}
      {reading && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-background-950/70 px-4"
          onClick={() => setReading(null)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-background-200/60 bg-background-100 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-play-circle-line text-xl" aria-hidden="true"></i>
              </span>
              <button
                type="button"
                onClick={() => setReading(null)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-400 hover:bg-background-200"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-50">
              开始阅读：{reading}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">
              你已进入《{work.title}》的阅读页面。接下来请跟随主角一起收集线索、破解谜案，享受烧脑反转的阅读体验。
            </p>
            <button
              type="button"
              onClick={() => setReading(null)}
              className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              开始追更
            </button>
          </div>
        </div>
      )}

      {/* 相关推荐 */}
      <div className="mt-14 border-t border-background-200/60 pt-10">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">
          <i className="ri-user-heart-line text-primary-400" aria-hidden="true"></i>
          猜你喜欢
        </h2>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {related.map((w) => (
            <WorkCard key={w.id} work={w} />
          ))}
        </div>
      </div>
    </div>
  );
}